import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Shuffle, ArrowLeft, Loader2, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { drawsAPI } from "../lib/api";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const drawSchema = z.object({
  title: z.string().min(3, "Titre requis (min 3 caractères)"),
  description: z.string().optional(),
  organizer_name: z.string().min(2, "Nom de l'organisateur requis"),
  organizer_contact: z.string().optional(),
  organizer_logo: z.string().url().optional().or(z.literal("")),
  bailiff_name: z.string().optional(),
  bailiff_info: z.string().optional(),
  withdrawal_instructions: z.string().optional(),
});

export default function CreateDraw() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(drawSchema),
    defaultValues: {
      organizer_name: user?.company_name || "",
      organizer_contact: user?.email || "",
      organizer_logo: "",
    }
  });

  const watchedLogo = watch("organizer_logo");

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setValue("organizer_logo", res.data.url);
      toast.success("Logo uploadé");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const res = await drawsAPI.create(data);
      toast.success("Tirage créé avec succès");
      navigate(`/draws/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Shuffle className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit' }}>Nouveau tirage</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Card className="bg-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Informations du tirage</CardTitle>
            <CardDescription>
              Configurez les informations de base de votre tirage au sort.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="title">Titre du tirage *</Label>
                  <Input
                    id="title"
                    placeholder="Ex: Grand tirage de Noël 2024"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    data-testid="draw-title-input"
                    {...register("title")}
                  />
                  {errors.title && (
                    <p className="text-sm text-destructive">{errors.title.message}</p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Décrivez votre tirage au sort..."
                    className="bg-slate-50 border-transparent focus:border-primary min-h-[100px]"
                    data-testid="draw-description-input"
                    {...register("description")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organizer_name">Nom de l'organisateur *</Label>
                  <Input
                    id="organizer_name"
                    placeholder="Nom de l'entreprise / association"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    data-testid="draw-organizer-input"
                    {...register("organizer_name")}
                  />
                  {errors.organizer_name && (
                    <p className="text-sm text-destructive">{errors.organizer_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organizer_contact">Contact organisateur</Label>
                  <Input
                    id="organizer_contact"
                    placeholder="email@exemple.com"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    {...register("organizer_contact")}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Logo</Label>
                  <input
                    type="file"
                    ref={logoInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleUploadLogo}
                  />
                  <div className="flex items-center gap-4">
                    {watchedLogo ? (
                      <div className="relative group">
                        <img 
                          src={watchedLogo} 
                          alt="Logo" 
                          className="h-12 object-contain rounded-lg bg-slate-100 px-2"
                        />
                        <button
                          type="button"
                          onClick={() => setValue("organizer_logo", "")}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="h-11"
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Uploader un logo
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bailiff_name">Huissier / Contrôleur</Label>
                  <Input
                    id="bailiff_name"
                    placeholder="Nom de l'huissier ou contrôleur"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    {...register("bailiff_name")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bailiff_info">Informations huissier</Label>
                  <Input
                    id="bailiff_info"
                    placeholder="Coordonnées ou numéro d'agrément"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    {...register("bailiff_info")}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="withdrawal_instructions">Consignes de retrait des lots</Label>
                  <Textarea
                    id="withdrawal_instructions"
                    placeholder="Indiquez comment les gagnants peuvent récupérer leurs lots..."
                    className="bg-slate-50 border-transparent focus:border-primary min-h-[100px]"
                    {...register("withdrawal_instructions")}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Link to="/dashboard">
                  <Button type="button" variant="outline" className="rounded-full">
                    Annuler
                  </Button>
                </Link>
                <Button 
                  type="submit" 
                  className="rounded-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="create-draw-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    "Créer le tirage"
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
